import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';

import { PayPalButtons } from "@paypal/react-paypal-js";

import { useAppContext } from '../AppContext';
import { useTranslation, Trans } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { IapService } from '../services/IapService';

function HarmonyProModal({ onClose }) {
    const { appFunctions, setMessage } = useAppContext();
    const { t } = useTranslation();
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [isPurchasingNative, setIsPurchasingNative] = useState(false);
    const isNative = Capacitor.getPlatform() !== 'web';

    const createSubscription = async () => {
        const createPayPalSubscription = httpsCallable(appFunctions, 'createPayPalSubscription');
        try {
            const result = await createPayPalSubscription();
            return result.data.subscriptionID;
        } catch (error) {
            console.error("Error creating PayPal subscription:", error);
            setMessage(t('harmonyProModal.subscriptionError', { message: error.message }));
        }
    };

    const onApprove = async (data) => {
        const activatePayPalSubscription = httpsCallable(appFunctions, 'activatePayPalSubscription');
        try {
            await activatePayPalSubscription({ subscriptionID: data.subscriptionID });
            setMessage(t('harmonyProModal.subscriptionSuccess'));
            onClose();
        } catch (error) {
            console.error("Error activating PayPal subscription:", error);
            setMessage(t('harmonyProModal.activationError', { message: error.message }));
        }
    };

    const handleNativeSubscribe = async () => {
        setIsPurchasingNative(true);
        try {
            const offerings = await IapService.getOfferings();
            if (offerings && offerings.current && offerings.current.availablePackages) {
                // For subscriptions, there is usually a package named 'pro_monthly' or similar. 
                // We'll assume the identifier is 'pro_subscription'
                const pkg = offerings.current.availablePackages.find(p => p.identifier === 'pro_subscription') || offerings.current.availablePackages[0];
                if (pkg) {
                    await IapService.purchasePackage(pkg);
                    setMessage(t('harmonyProModal.subscriptionSuccess'));
                    onClose();
                } else {
                    alert('Pro Subscription package not found.');
                }
            } else {
                alert('No packages available');
            }
        } catch (error) {
            console.error("Native subscribe failed", error);
            setMessage("Failed to subscribe via App Store");
        } finally {
            setIsPurchasingNative(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4" style={{ zIndex: 'var(--z-index-modal-backdrop)' }}>
            <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-glow max-w-md w-full text-white relative border border-purple-500/50 text-center">
                <h3 className="text-2xl font-bold mb-4 text-purple-300 font-playfair flex items-center justify-center"><LucideIcons.Crown size={24} className="mr-3 text-amber-300" /> {t('harmonyProModal.title')}</h3>
                <p className="text-gray-300 mb-6">{t('harmonyProModal.description')}</p>
                <ul className="space-y-3 text-left mb-8 text-gray-200">
                    <li className="flex items-center"><LucideIcons.CheckCircle2 size={18} className="text-green-400 mr-3" />
                        <Trans
                            i18nKey="harmonyProModal.feature1"
                            components={[<span className="font-bold text-sky-400" />]}
                        />
                    </li>
                    <li className="flex items-center"><LucideIcons.CheckCircle2 size={18} className="text-green-400 mr-3" />
                        <Trans
                            i18nKey="harmonyProModal.feature2"
                            components={[<span className="font-bold text-yellow-400" />]}
                        />
                    </li>
                    <li className="flex items-center"><LucideIcons.CheckCircle2 size={18} className="text-green-400 mr-3" />
                        <Trans
                            i18nKey="harmonyProModal.feature3"
                            components={[<span className="font-bold text-amber-300" />]}
                        />
                    </li>
                    <li className="flex items-center"><LucideIcons.CheckCircle2 size={18} className="text-green-400 mr-3" />
                        <Trans
                            i18nKey="harmonyProModal.feature4"
                            components={[<span className="font-bold text-purple-400" />]}
                        />
                    </li>
                </ul>
                {!isNative ? (
                    <PayPalButtons 
                        createSubscription={createSubscription}
                        onApprove={onApprove}
                        style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'subscribe' }}
                        intent="subscription"
                    />
                ) : (
                    <div className="flex flex-col items-center">
                        <button 
                            onClick={handleNativeSubscribe}
                            disabled={isPurchasingNative}
                            className="w-full mt-4 small-action-button bg-amber-600 hover:bg-amber-700 font-bold py-3 text-lg"
                        >
                            {isPurchasingNative ? 'Processing...' : `Subscribe with ${Capacitor.getPlatform() === 'ios' ? 'Apple' : 'Google'} Pay`}
                        </button>
                        
                        <button 
                            onClick={async () => {
                                try {
                                    setMessage("Restoring purchases...");
                                    await IapService.restorePurchases();
                                    setMessage("Purchases restored successfully!");
                                } catch (error) {
                                    setMessage("Failed to restore purchases.");
                                }
                            }}
                            className="w-full mt-2 text-sm text-gray-300 underline hover:text-white"
                        >
                            Restore Purchases
                        </button>

                        {Capacitor.getPlatform() === 'ios' && (
                            <div className="text-[10px] text-gray-400 mt-4 leading-tight">
                                <p>A $9.99/month purchase will be applied to your iTunes account on confirmation. Subscriptions will automatically renew unless canceled within 24-hours before the end of the current period. You can cancel anytime with your iTunes account settings. Any unused portion of a free trial will be forfeited if you purchase a subscription.</p>
                                <p className="mt-2">
                                    <a href="/terms" className="underline text-blue-400">Terms of Service</a> &bull; <a href="/privacy" className="underline text-blue-400">Privacy Policy</a>
                                </p>
                            </div>
                        )}
                    </div>
                )}
                <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition duration-300" aria-label={t('harmonyProModal.close')}><LucideIcons.X size={20} /></button>
            </div>
        </div>
    );
};

export default HarmonyProModal;
